<!--
/**
 * FeaturesWide Component - Advanced Teaser Management System
 *
 * This component displays up to 3 featured posts with an intelligent positioning system
 * that prioritizes permanent teasers from ACF options while ensuring no duplicates.
 *
 * PERMANENT TEASERS SYSTEM:
 * ========================
 *
 * 1. ACF CONFIGURATION:
 *    - Uses ACF Options Page with "Permanent Teasers" field group
 *    - Three URL fields: teasers_position_1, teasers_position_2, teasers_position_3
 *    - REST API endpoint: /wp-json/nvv/v1/permanent-teasers
 *
 * 2. POSITIONING LOGIC:
 *    Position 1: Priority to teasers_position_1, fallback to regular post
 *    Position 2: Priority to teasers_position_2, fallback to regular post
 *    Position 3: Priority to teasers_position_3, fallback to regular post
 *
 * 3. DUPLICATE PREVENTION:
 *    - Tracks used post IDs in a Set to prevent duplicates
 *    - Excludes permanent teasers that match the current article being viewed
 *    - If permanent teaser is already used, skips to next available regular post
 *    - If permanent teaser is the current article, skips to next available regular post
 *    - Regular posts are selected sequentially, skipping already used ones
 *
 * 4. DATA LOADING PROCESS:
 *    a) Load permanent teasers from ACF options
 *    b) Load categories for post categorization
 *    c) Load regular posts based on filter parameters
 *    d) Apply category assignments to regular posts
 *    e) Filter out excluded permalinks
 *    f) Merge permanent teasers with regular posts using positioning logic
 *
 * 5. FALLBACK SCENARIOS:
 *    - If permanent teaser API fails → uses regular posts only
 *    - If permanent teaser post loading fails → uses regular post for that position
 *    - If permanent teaser is duplicate → uses next available regular post
 *    - If permanent teaser is the current article → uses next available regular post
 *    - If no regular posts available → displays fewer than 3 posts
 *
 * 6. EXAMPLE SCENARIOS:
 *
 *    Scenario A - All positions filled with permanent teasers:
 *    Permanent: [Post A, Post B, Post C]
 *    Regular: [Post D, Post E, Post F]
 *    Result: [Post A (pos 1), Post B (pos 2), Post C (pos 3)]
 *
 *    Scenario B - Mixed permanent and regular:
 *    Permanent: [Post A, null, Post C]
 *    Regular: [Post D, Post E, Post F]
 *    Result: [Post A (pos 1), Post D (pos 2), Post C (pos 3)]
 *
 *    Scenario C - Duplicate permanent teaser:
 *    Permanent: [Post A, Post A, Post B]
 *    Regular: [Post C, Post D, Post E]
 *    Result: [Post A (pos 1), Post C (pos 2), Post B (pos 3)]
 *
 *    Scenario D - Permanent teaser conflicts with regular:
 *    Permanent: [Post A, null, null]
 *    Regular: [Post A, Post B, Post C]
 *    Result: [Post A (pos 1), Post B (pos 2), Post C (pos 3)]
 *
 *    Scenario E - Current article is permanent teaser:
 *    Current Article: Post A
 *    Permanent: [Post A, Post B, Post C]
 *    Regular: [Post D, Post E, Post F]
 *    Result: [Post D (pos 1), Post B (pos 2), Post C (pos 3)]
 *
 * 7. CLICK TRACKING SYSTEM:
 *    - Tracks every teaser click using Google Tag Manager (GTM)
 *    - Event name: 'teaser_click'
 *    - Tracked data includes:
 *      * teaser_type: 'permanent' or 'regular'
 *      * teaser_position: 1, 2, or 3 (display position)
 *      * post_title: Article title
 *      * post_slug: Article URL slug
 *      * post_id: WordPress post ID
 *      * component: 'FeaturesWide'
 *      * url: Current page URL
 *      * permanent_teaser_position: Original ACF position (1-3) for permanent teasers
 *    - Console logging for development debugging
 *    - Error handling to prevent tracking failures from breaking navigation
 *
 *
 */
-->

<template>

    <section
            class="features-wide"
            v-bind:class="{ 'isScrolledIntoView' : isReflowed && isScrolledIntoView }"
    >

        <div
                class="features-inner"
                v-if="posts.length"
        >

            <h2 class="heading" v-if="featuresHeading">
                {{ featuresHeading }}
            </h2>

            <div class="row">
                <div
                    v-if="posts.length"
                    class="col col-12 col-md-4 item"
                    v-for="(post, index) in posts"
                    :key="post.id"
                >
                    <div class="item-inner">
                        <div v-if="post.category" v-html="post.category.name" class="category" v-bind:class="'cat-label cat-label-'+post.category.slug" :style="[post.category.acf.category_color ? {'background-color': post.category.acf.category_color} : {}]"></div>
                        <div v-if="post.rating && post.rating > 0" v-html="post.rating+'/5'" class="rating" v-bind:class="'rating-label'"></div>
                        <div v-else v-html="'k.B.'" class="rating" v-bind:class="'rating-label'"></div>
                        <div class="item-image-container">
                            <article @click="handlePostClick(post, index + 1)" style="cursor: pointer;" role="button" tabindex="0">
                                <img
                                    v-if="post.acf && post.acf.config_disable_header !== true && post.acf.teaser_video_youtube_id"
                                    alt="Video"
                                    class="video-icon"
                                    src="/img/ic_video.svg"
                                />
                                <img
                                    v-if="post.acf && post.acf.teaser_image_960_720"
                                    :alt="post.acf.teaser_image_alt_text"
                                    class="item-image w-100"
                                    :src="wpApi.stripHost(post.acf.teaser_image_960_720)"
                                />
                            </article>
                        </div>
                        <div class="item-card">
                            <div class="item-card-inner">
                                <article @click="handlePostClick(post, index + 1)" style="cursor: pointer;" role="button" tabindex="0">
                                    <h4
                                        class="item-heading"
                                        v-html="post.acf && post.acf.teaser_title ? post.acf.teaser_title : post.title.rendered"
                                    ></h4>
                                    <p
                                        class="item-text"
                                        v-html="post.acf && post.acf.teaser_copy ? post.acf.teaser_copy : ''"
                                    ></p>
                                    <button class="btn btn-pink-white ani-btn-hover-scale-bounce">mehr erfahren</button>
                                </article>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

    </section>

</template>

<script>

    // console.log('Features');

    import smoothReflow from '../../../node_modules/vue-smooth-reflow/dist/vue-smooth-reflow.min';

    import cssMediaQueryHelper from '../services/cssMediaQueryHelper';
    import cssUnitHelper from '../services/cssUnitHelper';
    import loader from '../services/loader';
    import scrollHelper from '../services/scrollHelper';
    import wpApi from '../services/wpApi';

    import Vue from 'vue';

    export default {
        mixins : [smoothReflow],
        created(){
            // Remove SPA-specific event listener
            // this.$root.events.$on('route-transition', ()=>{
            //     this.scrollHandler();
            // });
            window.addEventListener('scroll', this.scrollHandler, { passive : true });
        },
        destroyed(){
            window.removeEventListener('scroll', this.scrollHandler);
            this.$el.removeEventListener('transitionend', this.transitionEndHandler);
            if (this.slickElement) {
                $(this.slickElement).off('afterChange', this.slickAfterChangeHandler);
                $(this.slickElement).off('beforeChange', this.slickBeforeChangeHandler);
            }
        },
        mounted(){
            this.$el.addEventListener('transitionend', this.transitionEndHandler);
            this.$smoothReflow({
                transition : 'height 800ms cubic-bezier(0.680, -0.550, 0.265, 1.550)',
            });
            this.scrollHandler();
        },
        components : {},
        data(){
            return {
                isReflowed : false,
                isScrolledIntoView : false,
                // posts: [], // REMOVED - comes from props now
                categories : [],
                permanentTeasers : null,
                scrollViewOffsetTopPx : cssUnitHelper.remToPx(4),
                slickElement : null,
                slickIsSliding : false,
                slickOptions : {
                    arrows : false,
                    infinite : false,
                    responsive : [
                        {
                            breakpoint : 767,
                            settings : {
                                slidesToScroll : 1,
                                slidesToShow : 1,
                            },
                        },
                    ],
                    slidesToScroll : 1,
                    slidesToShow : 3,
                },
                wpApi,
            };
        },
        props : {
            'posts': {
                default: () => {
                    return [];
                },
                type: Array,
            },
            'featuresExcludePostIds' : {
                default : ()=>{
                    return [];
                },
                type : Array,
            },
            'featuresExcludePostPermalinks' : {
                default : ()=>{
                    return [];
                },
                type : Array,
            },
            'featuresFilterParams' : {
				default : () => {
					return {};
				},
				type : Object,
			},
            'featuresForceLoading' : {
                default : false,
                type : Boolean,
            },
            'featuresHeading' : {
                default : '',
                type : String,
            },
            'featuresId' : {
                default : '',
                type : String,
            },
            'featuresMaxItems' : {
                default : 3,
                type : Number,
            },
        },
        methods : {
            handlePostClick(post, position) {

                // Call the tracking function
                this.trackPostClick(post, position);

                // Handle navigation with regular page load (non-SPA)
                window.location.href = post.link;
            },
            trackPostClick(post, position) {
                try {
                    // Determine the teaser type
                    const teaserType = post.isPermanentTeaser ? 'permanent' : 'regular';

                    // Get post title for tracking
                    const postTitle = post.title && post.title.rendered ? post.title.rendered : 'Unknown Title';

                    // Get post slug/permalink for tracking
                    const postSlug = post.link ? post.link.replace(wpApi.host + wpApi.baseUrl + '/', '').replace('/', '') : 'Unknown Slug';

                    // Track the click event using dataLayer
                    if (window.dataLayer) {
                        window.dataLayer.push({
                            event: 'teaser_click',
                            teaser_type: teaserType,
                            teaser_position: position,
                            post_title: postTitle,
                            post_slug: postSlug,
                            post_id: post.id,
                            component: 'FeaturesWide',
                            url: window.location.pathname.toLowerCase().replace(/\/$/, ""),
                            permanent_teaser_position: post.permanentTeaserPosition || null
                        });

                        console.log('GTM tracking sent:', {
                            event: 'teaser_click',
                            teaser_type: teaserType,
                            teaser_position: position,
                            post_title: postTitle
                        });
                    } else {
                        console.warn('window.dataLayer not available for tracking');
                    }

                } catch (error) {
                    console.error('Error tracking teaser click:', error);
                }
            },
            scrollHandler(){
                this.isScrolledIntoView = scrollHelper.isScrolledIntoView(
                    this.$el, this.scrollViewOffsetTopPx * cssMediaQueryHelper.watch.factor,
                );
                if (this.isScrolledIntoView === false) {
                    return;
                }
                window.removeEventListener('scroll', this.scrollHandler);
            },
            slickAfterChangeHandler(){
                this.slickIsSliding = false;
            },
            slickBeforeChangeHandler(){
                this.slickIsSliding = true;
            },
            slickInit(){
                if (this.slickElement === null) {
                    this.slickElement = this.$el.querySelector('.slick');
                    if (this.slickElement) {
                        $(this.slickElement).slick(this.slickOptions);
                        $(this.slickElement).on('afterChange', this.slickAfterChangeHandler);
                        $(this.slickElement).on('beforeChange', this.slickBeforeChangeHandler);
                    }
                }
            },
            slickItemClickHandler(route){
                // Changed from router.push to regular navigation
                if (this.slickIsSliding === false) {
                    window.location.href = route;
                }
            },
            transitionEndHandler(event){
                if (event.target === this.$el) {
                    this.isReflowed = true;
                }
            },
        },
        watch : {
            isLoaded(value){
                if (value) {
                    Vue.nextTick(()=>{
                        this.slickInit();
                    });
                }
            },
        },
    }

</script>

<style lang="scss">

    @import '../../css/variables';

    .features-wide {
        background-color: $color-nochvielvor-pink;
        overflow: hidden;
        will-change: height;
        margin-top: 1.5rem;
        @media screen and (min-width: 1200px) {
            margin-top: 1.5rem * $factor-1200;
        }
        @media screen and (min-width: 1440px) {
            margin-top: 1.5rem * $factor-1440;
        }
        @media screen and (min-width: 1920px) {
            margin-top: 1.5rem * $factor-1920;
        }
        @media screen and (min-width: 2400px) {
            margin-top: 1.5rem * $factor-2400;
        }
        &.isScrolledIntoView {
        }
        a {
            &,
            &:hover {
                text-decoration: none;
            }
        }
        .features-inner {
            padding: 2.5rem 1.5rem 3rem 1.5rem;
            @media screen and (min-width: 1200px) {
                padding: 2.5rem * $factor-1200 1.5rem * $factor-1200 3rem * $factor-1200 1.5rem * $factor-1200;
            }
            @media screen and (min-width: 1440px) {
                padding: 2.5rem * $factor-1440 1.5rem * $factor-1440 3rem * $factor-1440 1.5rem * $factor-1440;
            }
            @media screen and (min-width: 1920px) {
                padding: 2.5rem * $factor-1920 1.5rem * $factor-1920 3rem * $factor-1920 1.5rem * $factor-1920;
            }
            @media screen and (min-width: 2400px) {
                padding: 2.5rem * $factor-2400 1.5rem * $factor-2400 3rem * $factor-2400 1.5rem * $factor-2400;
            }
        }
        .heading {
            font-size: 22px;
            font-weight: bold;
            font-stretch: normal;
            font-style: normal;
            line-height: 28px;
            letter-spacing: normal;
            color: $color-nochvielvor-white;
            text-transform: uppercase;
            margin-bottom: 30px;
            @media screen and (min-width: 768px) {
                font-size: 30px;
                line-height: 40px;
                margin-bottom: 60px;
            }
        }
        .item {
            opacity: 1;
            transform: translateX(0) !important;
            transition: opacity 800ms, transform 800ms;
            .item-card-inner .btn{
                background-color: $color-nochvielvor-pink;
                border-color: $color-nochvielvor-pink;
                color: $color-nochvielvor-white;
            }
        }
        .container-fluid .row .col {
            @for $i from 1 through 20 {
                &:nth-child(#{$i}) {
                    .item {
                        transition-delay: #{$i * 200}ms;
                    }
                }
            }
            &:nth-child(even) {
                .item {
                    transform: translateX(100%);
                }
            }
            &:nth-child(odd) {
                .item {
                    transform: translateX(-100%);
                }
            }
        }
        .item {
            padding-bottom: 24px;
            @media screen and (min-width: 768px) {
                padding-bottom:30px;
            }
            .item-inner{
                background-color: $color-nochvielvor-white;
                height: 100%;
                flex-flow: column;
                display: flex;
                position:relative;
                .video-icon{
                    position: absolute;
                    left: 50%;
                    width: 58px;
                    height: 58px;
                    top:50%;
                    transform: translate(-50%, -50%);
                }
                .item-image-container{
                    max-height: 114px;
                    overflow: hidden;
                    position: relative;
                    @media screen and (min-width: 540px) {
                        max-height:125px;
                    }
                    @media screen and (min-width: 768px) {
                        max-height:145px;
                    }
                    @media screen and (min-width: 1200px) {
                        max-height: 145px * $factor-1200;
                    }
                    @media screen and (min-width: 1440px) {
                        max-height: 145px * $factor-1440;
                    }
                    @media screen and (min-width: 1920px) {
                        max-height: 145px * $factor-1920;
                    }
                    @media screen and (min-width: 2400px) {
                        max-height: 145px * $factor-2400;
                    }
                }
            }
            .item-card {
                transition: transform 400ms;
                will-change: transform;
                flex: 1 1 auto;
                .item-card-inner {
                    position: relative;
                    text-align: center;
                    height: 100%;
                    padding: 24px 16px 16px;
                    transition: transform 400ms;
                    will-change: transform;
                    @media screen and (min-width: 768px) {
                        padding:20px 22px 46px;
                    }
                    @media screen and (min-width: 1200px) {
                        padding:20px * $factor-1200 22px * $factor-1200 46px * $factor-1200;
                    }
                    @media screen and (min-width: 1440px) {
                        padding:20px * $factor-1200 22px * $factor-1440 46px * $factor-1440;
                    }
                    @media screen and (min-width: 1920px) {
                        padding:20px * $factor-1200 22px * $factor-1920 46px * $factor-1920;
                    }
                    @media screen and (min-width: 2400px) {
                        padding:20px * $factor-1200 22px * $factor-2400 46px * $factor-2400;
                    }
                    .btn{
                        @media screen and (min-width: 768px) {
                            position: absolute;
                            bottom: 0;
                            transform: translate(-50%, -50%);
                        }
                    }

                }
                .item-heading {
                    margin-bottom: 14px;
                    color: $color-nochvielvor-black;
                    font-size: 18px;
                    font-weight: bold;
                    line-height: 1.2;
                    text-transform: uppercase;
                    @media screen and (min-width: 768px) {
                        margin-bottom: 12px;
                    }
                    @media screen and (min-width: 1200px) {
                        font-size: 15px * $factor-1200;
                        margin-bottom: 12px * $factor-1200;
                    }
                    @media screen and (min-width: 1440px) {
                        font-size: 15px * $factor-1440;
                        margin-bottom: 12px * $factor-1440;
                    }
                    @media screen and (min-width: 1920px) {
                        font-size: 15px * $factor-1920;
                        margin-bottom: 12px * $factor-1920;
                    }
                    @media screen and (min-width: 2400px) {
                        font-size: 15px * $factor-2400;
                        margin-bottom: 12px * $factor-2400;
                    }
                }
                .item-text {
                    //max-height: 14px * 6;
                    color: $color-nochvielvor-black;
                    font-size: 12px;
                    line-height: 1.2;
                    margin-bottom: 14px;
                    @media screen and (min-width: 768px) {
                        font-size: 12px;
                        margin-bottom: 22px;
                    }
                    @media screen and (min-width: 1200px) {
                        font-size: 12px * $factor-1200;
                    }
                    @media screen and (min-width: 1440px) {
                        font-size: 12px * $factor-1440;
                    }
                    @media screen and (min-width: 1920px) {
                        font-size: 12px * $factor-1920;
                    }
                    @media screen and (min-width: 2400px) {
                        font-size: 12px * $factor-2400;
                    }
                }
            }
            .cat-label {
                background-color: $color-nochvielvor-black;
                color: $color-nochvielvor-white;
                text-transform: uppercase;
                font-weight: bold;
                position: absolute;
                padding: 5px;
                font-size:15px;
                z-index: 1;
                &.cat-label-hiv-im-alltag{
                    background-color: $color-nochvielvor-blue;
                }
                &.cat-label-zukunft-mit-hiv{
                    background-color: rgba(231, 62, 80, 0.8);
                }
                &.cat-label-hiv-im-dialog{
                    background-color: $color-nochvielvor-gray;
                }
            }
            .rating-label{
                background-color: $color-nochvielvor-white;
                color: $color-nochvielvor-pink;
                text-transform: uppercase;
                font-weight: bold;
                position: absolute;
                background-color: #fff;
                background-image: url(/img/ic_star.svg);
                background-repeat: no-repeat;
                padding:7px 7px 7px 27px;
                right: 0;
                background-position: 7px 7px;
                z-index: 1;
            }
        }
    }

</style>
